/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office */

const { PHONE_CODES, COUNTRIES } = require("./form-data.js");

// API config - from env (.env) or defaults. Injected at build time via webpack DefinePlugin.
const API_CONFIG = {
  baseUrl: process.env.TOI_API_BASE_URL || "https://espotoi.husky.aegona.net",
  activeCasesPath: "/api/v1/PublicCase/action/active",
  linkEmailPath: "/api/v1/PublicCase/action/link",
  // Matches backend method PublicCase::getActionLinkStatus
  linkStatusPath: "/api/v1/PublicCase/action/linkStatus",
  createCasePath: "/api/v1/PublicCase/action/create",
  apiKey: "17aacc1f23d9f43591e9bf14e92a071e",
};

function stripHtmlToText(html) {
  if (!html) return "";
  const withBreaks = String(html)
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\s*\/(?:td|th|tr)\s*>/gi, "\n")
    .replace(/<\/\s*p\s*>/gi, "\n")
    .replace(/<\/\s*div\s*>/gi, "\n")
    .replace(/<\s*li\s*>/gi, "\n- ");
  const text = withBreaks.replace(/<[^>]+>/g, " ");
  return text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\r/g, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function extractFieldByLabel(sourceText, labels) {
  if (!sourceText) return "";
  for (const label of labels) {
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");
    // A label must start a line; otherwise "Hospital" inside
    // "Hanoi French Hospital" can be mistaken for a new field label.
    const regex = new RegExp(`(?:^|\\n)\\s*${escaped}(?![A-Za-z])\\s*[:#-]?\\s*([^\\n]+)`, "i");
    const match = sourceText.match(regex);
    if (match && match[1]) {
      let val = match[1].trim();
      // Fast cleanup for known parenthesized explanations if they leaked into the value
      val = val.replace(/^\((?:City|Country|State) in which the patient is seeking care\)\s*[:#-]?\s*/i, "");
      // Email templates sometimes put Provider/Hospital, Email and Phone in one table cell.
      // Keep only the value before the next contact field label.
      val = val.split(/\s+(?=(?:E-?mail|Phone(?:\s+Number)?|Mobile|Fax)\s*[:#-])/i)[0].trim();
      return val.trim();
    }
  }
  return "";
}

function extractClaimData({ subject, bodyHtml }) {
  const bodyText = stripHtmlToText(bodyHtml);
  const source = `${subject || ""}\n${bodyText}`;

  // Feature note: supports common claim/case labels seen in partner emails.
  const claimRegex =
    /(?:(?:GEM\s*)?Case\/Claim\s*Reference\s*Number|Claim\s*(?:No\b|Number\b)|Case\s*(?:No\b|Number\b)|Reference\s*Number|(?:Claim|Case)\s*[:#-])\s*[:#-]?\s*([A-Za-z0-9-]+)/i;
  const claimMatch = source.match(claimRegex);
  const claimNo = claimMatch?.[1]?.trim() || "";

  const patientFullNameRaw = extractFieldByLabel(bodyText, [
    "Patient Title, Patient First Name, Patient Last Name",
    "Patient Name",
  ]);
  const patientFirstName = extractFieldByLabel(bodyText, ["Patient First Name"]);
  const patientLastName = extractFieldByLabel(bodyText, ["Patient Last Name"]);
  const patientName =
    patientFullNameRaw ||
    [patientFirstName, patientLastName]
      .filter(Boolean)
      .join(" ")
      .trim();

  const claimCountry = extractFieldByLabel(bodyText, [
    "Claim country (Country in which the patient is seeking care)",
    "Claim country",
  ]);
  const claimCity = extractFieldByLabel(bodyText, [
    "Claim city (City in which the patient is seeking care)",
    "Claim city",
  ]);
  const claimState = extractFieldByLabel(bodyText, [
    "Claim state (State in which the patient is seeking care)",
    "Claim state",
  ]);

  return {
    claimNo,
    insuranceCompany: extractFieldByLabel(bodyText, [
      "Insurance Companies",
      "Insurance Company",
      "Insurer",
      "Organization",
    ]),
    policyNo: extractFieldByLabel(bodyText, ["Policy Number", "Policy No"]),
    patientName,
    dateOfBirth: extractFieldByLabel(bodyText, [
      "Date of Birth (DD/MM/YYYY)",
      "Date of Birth",
      "Date Of Birth",
      "Patient Date of Birth",
      "Patient DOB",
      "DOB",
      "Birth Date",
    ]),
    problemType:
      extractFieldByLabel(bodyText, ["Medical Condition (symptoms/complaint)"]) || "Medical",
    callDate: extractFieldByLabel(bodyText, ["Incident Date"]),
    locationCountry: claimCountry || "",
    locationCity: claimCity || claimState || "",
    hospital: extractFieldByLabel(bodyText, [
      "Hospital Name",
      "Hospital/Clinic",
      "Hospitals",
      "Hospital",
      "Medical Provider",
      "Treatment Provider",
      "Provider",
    ]),
    rawText: bodyText,
  };
}

// Mock mailbox: only when ?mock=1 in URL (for local test without Graph consent).
function useMockMailbox() {
  if (typeof window !== "undefined" && window.location && window.location.search) {
    const params = new URLSearchParams(window.location.search);
    if (params.get("mock") === "1") return true;
  }
  return false;
}

Office.onReady((info) => {
  if (info.host === Office.HostType.Outlook) {
    document.getElementById("sideload-msg").style.display = "none";
    document.getElementById("app-body").style.display = "flex";
    initDashboard();
  }
});

function initDashboard() {
  const mockNotice = document.getElementById("mock-mailbox-notice");
  if (mockNotice && useMockMailbox()) {
    mockNotice.style.display = "block";
    mockNotice.textContent = "Local test mode (mock). Form prefilled — click Create to send. Set mock user to your Espo user email in code if needed.";
  }

  // Prefill create form in mock mode so Create button is enabled without manual fill (for testing).
  const prefillCreateFormForMock = () => {
    if (!useMockMailbox()) return;
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    const dateStr = `${y}-${m}-${d}`;
    const dateTimeStr = `${dateStr}T${String(today.getHours()).padStart(2, "0")}:${String(today.getMinutes()).padStart(2, "0")}`;
    const endYear = new Date(today);
    endYear.setFullYear(endYear.getFullYear() + 1);
    const endStr = `${endYear.getFullYear()}-${String(endYear.getMonth() + 1).padStart(2, "0")}-${String(endYear.getDate()).padStart(2, "0")}`;
    if (createInsuranceCompany) createInsuranceCompany.value = "Test Insurance";
    if (createPolicyNo) createPolicyNo.value = "POL-001";
    if (createPolicyStart) createPolicyStart.value = dateStr;
    if (createPolicyEnd) createPolicyEnd.value = endStr;
    if (createPatientName) createPatientName.value = "Test Patient";
    if (createCallDate) createCallDate.value = dateTimeStr;
    if (createLocationCountry) createLocationCountry.value = createLocationCountry.options?.[0]?.value || "Afghanistan";
    if (createLocationCity) createLocationCity.value = "Test City";
  };

  const caseCombo = document.getElementById("case-combo");
  const caseInput = document.getElementById("case-combobox");
  const caseOptions = document.getElementById("case-options");
  const caseSelect = document.getElementById("case-select");
  const linkDetails = document.getElementById("link-details");
  const linkDocType = document.getElementById("link-doc-type");
  const linkBtn = document.getElementById("link-btn");
  const openCaseBtn = document.getElementById("open-case-btn");
  const linkStatus = document.getElementById("link-status");
  const caseField = caseCombo ? caseCombo.closest(".field") : null;
  const linkDocTypeField = linkDocType ? linkDocType.closest(".field") : null;
  const createShortcut = document.getElementById("create-shortcut");
  const createSection = document.getElementById("create-section");
  const linkSection = document.getElementById("link-section");
  const createClose = document.getElementById("create-close");
  const createBtn = document.getElementById("create-btn");
  const createStatus = document.getElementById("create-status");
  const createPriority = document.getElementById("create-priority");
  const createStatusSelect = document.getElementById("status");
  const createExternalCaseCode = document.getElementById("create-external-case-code");
  const createInsuranceCompany = document.getElementById("create-insurance-company");
  const createHospital = document.getElementById("create-hospital");
  const createPolicyNo = document.getElementById("create-policy-no");
  const createPolicyStart = document.getElementById("create-policy-start");
  const createPolicyEnd = document.getElementById("create-policy-end");
  const createPatientName = document.getElementById("create-patient-name");
  const createDateOfBirth = document.getElementById("create-date-of-birth");
  const createPatientType = document.getElementById("create-patient-type");
  const createCaseCategory = document.getElementById("create-case-category");
  const createProblemType = document.getElementById("create-problem-type");
  const createCallDate = document.getElementById("create-call-date");
  const createPhoneType = document.getElementById("create-phone-type");
  const createPhoneCountry = document.getElementById("create-phone-country");
  const createPhoneNumber = document.getElementById("create-phone-number");
  const createLocationCountry = document.getElementById("create-location-country");
  const createLocationCity = document.getElementById("create-location-city");
  const createDocType = document.getElementById("create-doc-type");
  let allCases = [];
  let isCreateLoading = false;
  let linkedCaseDetailUrl = "";
  let emailAlreadyLinked = false;
  let detectedClaimData = null;
  let emailContextPromise = null;

  // Populate phone country and location country from form-data (keeps HTML lean)
  PHONE_CODES.forEach(([value, label], i) => {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = label;
    if (i === 0) opt.selected = true;
    createPhoneCountry.appendChild(opt);
  });
  COUNTRIES.forEach((name, i) => {
    const opt = document.createElement("option");
    opt.value = name;
    opt.textContent = name;
    if (i === 0) opt.selected = true;
    createLocationCountry.appendChild(opt);
  });

  const closeCaseOptions = () => {
    caseOptions.classList.remove("is-open");
    caseInput.setAttribute("aria-expanded", "false");
  };

  const openCaseOptions = () => {
    caseOptions.classList.add("is-open");
    caseInput.setAttribute("aria-expanded", "true");
  };

  const updateLinkState = () => {
    const hasCase = caseSelect.value !== "";
    const hasDocType = linkDocType.value !== "";
    const effectiveHasCase = hasCase || emailAlreadyLinked;

    if (emailAlreadyLinked) {
      if (caseField) caseField.style.display = "none";
      if (linkDocTypeField) linkDocTypeField.style.display = "none";
      // Hide create flow entirely when email is already linked.
      if (createShortcut) createShortcut.style.display = "none";
      if (createSection) createSection.style.display = "none";
      if (linkSection) linkSection.style.display = "flex";
    } else {
      if (caseField) caseField.style.display = "";
      if (linkDocTypeField) linkDocTypeField.style.display = "";
      if (createShortcut) createShortcut.style.display = "inline-flex";
    }

    linkDetails.style.display = effectiveHasCase ? "flex" : "none";
    linkDetails.setAttribute("aria-hidden", effectiveHasCase ? "false" : "true");
    linkDetails.classList.toggle("stack--muted", !effectiveHasCase);
    linkDocType.disabled = !effectiveHasCase;

    if (emailAlreadyLinked) {
      linkBtn.disabled = true;
      linkBtn.textContent = "Linked";
    } else {
      linkBtn.disabled = !hasCase || !hasDocType;
      linkBtn.textContent = "Link to case";
    }
  };

  const setLinkStatus = (message, status) => {
    linkStatus.textContent = message;
    linkStatus.classList.remove("status--success", "status--error");
    if (status) {
      linkStatus.classList.add(status);
    }
  };

  const setOpenCaseState = (detailUrl) => {
    linkedCaseDetailUrl = detailUrl || "";
    if (!openCaseBtn) return;
    openCaseBtn.style.display = linkedCaseDetailUrl ? "inline-flex" : "none";
    openCaseBtn.disabled = !linkedCaseDetailUrl;
  };

  if (openCaseBtn) {
    openCaseBtn.addEventListener("click", () => {
      if (!linkedCaseDetailUrl) return;
      window.open(linkedCaseDetailUrl, "_blank", "noopener,noreferrer");
    });
  }

  caseSelect.addEventListener("change", updateLinkState);
  linkDocType.addEventListener("change", updateLinkState);
  updateLinkState();

  const renderCases = (casesToRender) => {
    const selectedValue = caseSelect.value;
    caseSelect.innerHTML = '<option value="">Select Case</option>';
    caseOptions.innerHTML = "";

    casesToRender.forEach((caseItem, index) => {
      const option = document.createElement("option");
      option.value = String(caseItem.value);
      option.textContent = caseItem.label;
      caseSelect.appendChild(option);

      const listItem = document.createElement("li");
      listItem.className = "combo__option";
      listItem.setAttribute("role", "option");
      listItem.dataset.value = String(caseItem.value);
      listItem.textContent = caseItem.label;
      listItem.addEventListener("click", () => {
        caseSelect.value = String(caseItem.value);
        caseInput.value = caseItem.label;
        closeCaseOptions();
        updateLinkState();
      });
      if (index === 0) {
        listItem.classList.add("is-active");
      }
      caseOptions.appendChild(listItem);
    });

    if (selectedValue) {
      caseSelect.value = selectedValue;
      const selectedOption = caseSelect.selectedOptions[0];
      if (selectedOption) {
        caseInput.value = selectedOption.textContent;
      }
    } else {
      caseInput.value = "";
    }

    updateLinkState();
  };

  const normalizeCases = (payload) => {
    const list =
      (Array.isArray(payload) && payload) ||
      payload?.data ||
      payload?.items ||
      payload?.result ||
      [];

    if (!Array.isArray(list)) {
      return [];
    }

    return list.map((entry, index) => {
      const id = entry?.id || entry?.caseId || entry?.case_id || entry?.code || entry?.caseNumber;
      const number = entry?.caseNumber || entry?.number || entry?.code || entry?.case_code;
      const title = entry?.title || entry?.name || entry?.subject || entry?.summary;
      const fallbackLabel = `Case ${index + 1}`;
      const labelParts = [];

      if (id) {
        labelParts.push(String(id));
      } else if (number) {
        labelParts.push(String(number));
      }

      if (title) {
        labelParts.push(title);
      }

      return {
        value: id || number || `case-${index + 1}`,
        label: labelParts.length ? labelParts.join(" - ") : fallbackLabel,
        id: id ? String(id) : "",
        caseCode: (entry?.case_id || entry?.code || number || "").toString(),
        detailUrl: entry?.detail_url || entry?.detailUrl || "",
      };
    });
  };

  const buildCasesUrl = ({ idSearch, caseCodeSearch } = {}) => {
    const baseUrl = `${API_CONFIG.baseUrl}${API_CONFIG.activeCasesPath}`;
    const params = new URLSearchParams();
    if (idSearch) {
      params.set("id", idSearch);
    }
    if (caseCodeSearch) {
      params.set("case_code", caseCodeSearch);
    }
    if (!params.toString()) {
      return baseUrl;
    }
    return `${baseUrl}?${params.toString()}`;
  };

  const fetchCases = async (idSearch) => {
    const url = buildCasesUrl({ idSearch });
    // eslint-disable-next-line no-console
    console.log("[TOI Case] Fetching cases:", url);
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "X-Api-Key": API_CONFIG.apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load cases: ${response.status}`);
      }

      const payload = await response.json();
      const cases = normalizeCases(payload);
      // eslint-disable-next-line no-console
      console.log("[TOI Case] Cases loaded:", cases.length, cases);
      return cases;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TOI Case] Case list fetch failed", error);
      return [];
    }
  };

  const fetchCasesByCaseCode = async (caseCodeSearch) => {
    if (!caseCodeSearch) return [];
    const url = buildCasesUrl({ caseCodeSearch });
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "X-Api-Key": API_CONFIG.apiKey,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to load cases: ${response.status}`);
      }
      const payload = await response.json();
      return normalizeCases(payload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TOI Case] Case lookup by code failed", error);
      return [];
    }
  };

  const getInternetMessageId = () => {
    const item = Office.context.mailbox.item;
    if (typeof item.getInternetMessageIdAsync === "function") {
      return new Promise((resolve, reject) => {
        item.getInternetMessageIdAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
            return;
          }
          reject(result.error);
        });
      });
    }
    return Promise.resolve(item.internetMessageId || "");
  };

  const getBodyHtml = () =>
    new Promise((resolve) => {
      Office.context.mailbox.item.body.getAsync(Office.CoercionType.Html, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
          return;
        }
        resolve("");
      });
    });

  const getAttachments = () => {
    const item = Office.context.mailbox.item;
    const attachments = item.attachments || [];
    if (!attachments.length) {
      return Promise.resolve([]);
    }

    return Promise.all(
      attachments.map(
        (attachment) =>
          new Promise((resolve) => {
            item.getAttachmentContentAsync(attachment.id, (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                const contentBytes =
                  result.value.format === Office.MailboxEnums.AttachmentContentFormat.Base64
                    ? result.value.content
                    : "";
                resolve({
                  name: attachment.name,
                  contentType: attachment.contentType,
                  size: attachment.size,
                  contentBytes,
                });
                return;
              }
              resolve({
                name: attachment.name,
                contentType: attachment.contentType,
                size: attachment.size,
              });
            });
          })
      )
    );
  };

  const getMockEmailContext = () => {
    const now = new Date().toISOString();
    const uniqueId = "mock-" + Date.now() + "-" + Math.random().toString(36).slice(2, 9);
    return {
      internetMessageId: uniqueId,
      graphMessageId: "",
      email: {
        subject: "Local test (mock mailbox)",
        from: { name: "Test Sender", email: "test@local.dev" },
        to: ["recipient@local.dev"],
        cc: [],
        received_at: now,
        body: { content_type: "html", content: "<p>Mock body for local testing.</p>" },
      },
      attachments: [],
      context: { user: "admin@yourcompany.com", source: "outlook_addin_mock" },
    };
  };

  const buildEmailContext = async () => {
    if (useMockMailbox()) {
      return getMockEmailContext();
    }
    const item = Office.context.mailbox.item;
    const internetMessageId = await getInternetMessageId();
    const bodyHtml = await getBodyHtml();
    const attachments = await getAttachments();
    const graphMessageId =
      item.itemId && Office.context.mailbox.convertToRestId
        ? Office.context.mailbox.convertToRestId(
            item.itemId,
            Office.MailboxEnums.RestVersion.v2_0
          )
        : "";
    const receivedDate =
      item.dateTimeReceived || item.dateTimeCreated || item.dateTimeModified || null;

    return {
      internetMessageId,
      graphMessageId,
      email: {
        subject: item.subject || "",
        from: item.from
          ? { name: item.from.displayName || "", email: item.from.emailAddress || "" }
          : { name: "", email: "" },
        to: (item.to || []).map((entry) => entry.emailAddress || ""),
        cc: (item.cc || []).map((entry) => entry.emailAddress || ""),
        received_at: receivedDate ? new Date(receivedDate).toISOString() : "",
        body: {
          content_type: "html",
          content: bodyHtml || "",
        },
      },
      attachments,
      context: {
        user: Office.context.mailbox.userProfile.emailAddress || "",
        source: "outlook_addin",
      },
    };
  };

  const getEmailContextCached = async () => {
    if (!emailContextPromise) {
      emailContextPromise = buildEmailContext();
    }
    return emailContextPromise;
  };

  const normalizeToDateTimeLocal = (value) => {
    if (!value) return "";
    const direct = new Date(value);
    if (!Number.isNaN(direct.getTime())) {
      const y = direct.getFullYear();
      const m = String(direct.getMonth() + 1).padStart(2, "0");
      const d = String(direct.getDate()).padStart(2, "0");
      const hh = String(direct.getHours()).padStart(2, "0");
      const mm = String(direct.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${d}T${hh}:${mm}`;
    }
    const dateOnlyMatch = String(value).match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (!dateOnlyMatch) return "";
    const month = String(dateOnlyMatch[1]).padStart(2, "0");
    const day = String(dateOnlyMatch[2]).padStart(2, "0");
    const year = dateOnlyMatch[3];
    return `${year}-${month}-${day}T00:00`;
  };

  const normalizeToDateInput = (value) => {
    if (!value) return "";
    const text = String(value).trim();
    const isoMatch = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (isoMatch) {
      return `${isoMatch[1]}-${String(isoMatch[2]).padStart(2, "0")}-${String(isoMatch[3]).padStart(2, "0")}`;
    }
    const dateMatch = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (!dateMatch) return "";
    const first = Number(dateMatch[1]);
    const second = Number(dateMatch[2]);
    const day = first > 12 ? first : second;
    const month = first > 12 ? second : first;
    return `${dateMatch[3]}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };

  const buildAutoCreatePayloadFromDetected = async (detected) => {
    const emailContext = await getEmailContextCached();
    return {
      priority: createPriority.value || "Normal",
      status: createStatusSelect.value || "Normal",
      external_case_code: detected.claimNo || "",
      insurance_company: detected.insuranceCompany || "",
      hospital: detected.hospital || createHospital.value.trim() || "",
      policy_no: detected.policyNo || "",
      policy_start_date: createPolicyStart.value || "",
      policy_end_date: createPolicyEnd.value || "",
      patient_name: detected.patientName || createPatientName.value.trim() || "",
      date_of_birth: normalizeToDateInput(detected.dateOfBirth) || createDateOfBirth.value || "",
      patient_type: createPatientType.value || "Normal",
      case_category: createCaseCategory.value || "Outpatient",
      problem_type: detected.problemType || createProblemType.value || "Medical",
      call_date: normalizeToDateTimeLocal(detected.callDate) || createCallDate.value || "",
      mobile: {
        type: createPhoneType.value || "Office",
        country_code: createPhoneCountry.value || "+1",
        number: createPhoneNumber.value.trim(),
      },
      location_country: detected.locationCountry || createLocationCountry.value || "",
      location_city: detected.locationCity || createLocationCity.value.trim() || "",
      doc_type: createDocType.value || "New request / Guarantee",
      ...emailContext,
    };
  };

  const prefillCreateFormFromDetected = (detected) => {
    if (!detected) return;
    // Feature note: prefill visible fields so reviewer can verify extracted values.
    if (detected.claimNo && !createExternalCaseCode.value) createExternalCaseCode.value = detected.claimNo;
    if (detected.insuranceCompany && !createInsuranceCompany.value) {
      createInsuranceCompany.value = detected.insuranceCompany;
    }
    if (detected.policyNo && !createPolicyNo.value) createPolicyNo.value = detected.policyNo;
    if (detected.patientName && !createPatientName.value) createPatientName.value = detected.patientName;
    const dateOfBirth = normalizeToDateInput(detected.dateOfBirth);
    if (dateOfBirth && createDateOfBirth && !createDateOfBirth.value) {
      createDateOfBirth.value = dateOfBirth;
    }
    if (detected.hospital && createHospital && !createHospital.value) {
      createHospital.value = detected.hospital;
    }
    const callDate = normalizeToDateTimeLocal(detected.callDate);
    if (callDate && !createCallDate.value) createCallDate.value = callDate;
    if (detected.locationCountry && !createLocationCountry.value) {
      createLocationCountry.value = detected.locationCountry;
    }
    if (detected.locationCity && !createLocationCity.value) createLocationCity.value = detected.locationCity;
  };

  const buildLinkPayload = async () => {
    const emailContext = await getEmailContextCached();
    return {
      case_id: caseSelect.value,
      doc_type: linkDocType.value,
      ...emailContext,
    };
  };

  const fetchLinkStatus = async () => {
    // If backend doesn't implement this yet, this will safely no-op (and keep UI unchanged).
    // NOTE: baseUrl can be empty in dev (proxy mode), so don't require it here.
    // eslint-disable-next-line no-console
    console.log("[TOI Case] Checking existing link status...");

    if (!API_CONFIG.linkStatusPath || !API_CONFIG.apiKey) {
      return false;
    }

    try {
      const emailContext = await getEmailContextCached();
      const internetMessageId = emailContext?.internetMessageId || "";
      if (!internetMessageId) return false;

      const url = `${API_CONFIG.baseUrl || ""}${API_CONFIG.linkStatusPath}?internet_message_id=${encodeURIComponent(
        internetMessageId
      )}`;

      // eslint-disable-next-line no-console
      console.log("[TOI Case] Link-status URL", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "X-Api-Key": API_CONFIG.apiKey,
        },
      });

      const body = await response.json().catch(() => null);
      if (!response.ok || !body) {
        // eslint-disable-next-line no-console
        console.error("[TOI Case] Link-status HTTP error", response.status, body);
        return false;
      }

      const payload = body?.data ?? body;
      const linked = Boolean(payload?.linked);
      const caseInfo = payload?.case ?? payload?.data ?? null;
      const caseId = caseInfo?.id || caseInfo?.case_id || "";
      const detailUrl = caseInfo?.detail_url || caseInfo?.detailUrl || "";

      // eslint-disable-next-line no-console
      console.log("[TOI Case] Link-status response", { linked, caseId, detailUrl, raw: body });

      if (linked && detailUrl) {
        emailAlreadyLinked = true;
        setOpenCaseState(detailUrl);
        if (caseId) {
          setLinkStatus(`This email is already linked to case: '${caseId}'.`, "status--success");
        } else {
          setLinkStatus("This email is already linked to a case.", "status--success");
        }
        updateLinkState();
        return true;
      }

      emailAlreadyLinked = false;
      setOpenCaseState("");
      updateLinkState();
      return false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TOI Case] Link-status check failed", error);
      setOpenCaseState("");
      return false;
    }
  };

  const buildCreatePayload = async (overrides = {}) => {
    const emailContext = await getEmailContextCached();
    return {
      priority: createPriority.value,
      status: createStatusSelect.value || "Normal",
      external_case_code: createExternalCaseCode.value.trim(),
      insurance_company: createInsuranceCompany.value.trim(),
      hospital: createHospital.value.trim(),
      policy_no: createPolicyNo.value.trim(),
      policy_start_date: createPolicyStart.value,
      policy_end_date: createPolicyEnd.value,
      patient_name: createPatientName.value.trim(),
      date_of_birth: createDateOfBirth.value,
      patient_type: createPatientType.value,
      case_category: createCaseCategory.value,
      problem_type: createProblemType.value,
      call_date: createCallDate.value,
      mobile: {
        type: createPhoneType.value,
        country_code: createPhoneCountry.value,
        number: createPhoneNumber.value.trim(),
      },
      location_country: createLocationCountry.value,
      location_city: createLocationCity.value.trim(),
      doc_type: createDocType.value,
      ...emailContext,
      ...overrides,
    };
  };

  const autoCreateCaseFromDetected = async (detected) => {
    if (!detected?.claimNo || !API_CONFIG.createCasePath) return null;
    try {
      const payload = await buildAutoCreatePayloadFromDetected(detected);
      const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.createCasePath}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": API_CONFIG.apiKey,
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json().catch(() => null);
      if (!response.ok || responseBody?.status === "error") {
        return null;
      }
      return {
        id: responseBody?.data?.id || "",
        detailUrl: responseBody?.data?.detail_url || responseBody?.data?.detailUrl || "",
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TOI Case] Auto-create from detection failed", error);
      return null;
    }
  };

  const bootstrapClaimDetection = async () => {
    try {
      const emailContext = await getEmailContextCached();
      detectedClaimData = extractClaimData({
        subject: emailContext?.email?.subject || "",
        bodyHtml: emailContext?.email?.body?.content || "",
      });
      prefillCreateFormFromDetected(detectedClaimData);
      updateCreateState();

      if (!detectedClaimData?.claimNo || emailAlreadyLinked) return;
      const matchedCases = await fetchCasesByCaseCode(detectedClaimData.claimNo);
      if (matchedCases.length === 1) {
        const matchedCase = matchedCases[0];
        caseSelect.value = String(matchedCase.value);
        caseInput.value = matchedCase.label;
        if (matchedCase.detailUrl) {
          setOpenCaseState(matchedCase.detailUrl);
        }
        setLinkStatus(
          `Detected Claim No '${detectedClaimData.claimNo}'. Existing case found, you can link manually.`,
          "status--success"
        );
        updateLinkState();
        showLink();
        return;
      }

      if (matchedCases.length > 1) {
        renderCases(matchedCases);
        setLinkStatus(
          `Detected Claim No '${detectedClaimData.claimNo}' with multiple matches. Please select one case.`,
          ""
        );
        return;
      }

      // Feature behavior: when no case is matched, open create form automatically for review.
      showCreate();
      setLinkStatus(
        `Detected Claim No '${detectedClaimData.claimNo}'. No existing case found, please create case.`,
        ""
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TOI Case] Claim detection bootstrap failed", error);
    }
  };

  const postLinkToCase = async ({ auto = false } = {}) => {
    if (!API_CONFIG.linkEmailPath) {
      // eslint-disable-next-line no-console
      console.error("Missing API_CONFIG.linkEmailPath");
      return;
    }

    linkBtn.disabled = true;
    linkBtn.textContent = "Linking...";
    setLinkStatus(auto ? "Auto-linking reply email to case..." : "Linking email to case...", "");
    try {
      const payload = await buildLinkPayload();
      // eslint-disable-next-line no-console
      console.log("Link to case payload", payload);
      const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.linkEmailPath}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": API_CONFIG.apiKey,
        },
        body: JSON.stringify(payload),
      });

      const responseBody = await response.json().catch(() => null);
      const errorMessage = responseBody?.error || `Link failed: ${response.status}`;

      if (!response.ok) {
        throw new Error(errorMessage);
      }

      if (responseBody?.status === "error") {
        throw new Error(errorMessage);
      }

      const caseId = responseBody?.data?.id;
      const detailUrl = responseBody?.data?.detail_url ?? responseBody?.data?.detailUrl ?? "";

      emailAlreadyLinked = true;
      if (detailUrl) {
        setOpenCaseState(detailUrl);
      } else {
        // Backend might not return a URL yet; try to re-check status.
        fetchLinkStatus();
      }

      if (caseId) {
        setLinkStatus(
          auto ? `Reply email auto-linked to case: '${caseId}'.` : `Linked successfully case: '${caseId}'.`,
          "status--success"
        );
      } else {
        setLinkStatus("Linked successfully.", "status--success");
      }
      updateLinkState();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Link to case failed", error);
      setLinkStatus(
        auto
          ? error?.message || "Auto-link failed. Please use Link to case manually."
          : error?.message || "Link failed. Please try again.",
        "status--error"
      );
    } finally {
      linkBtn.textContent = "Link to case";
      updateLinkState();
    }
  };

  const setCreateStatus = (message, status) => {
    createStatus.textContent = message;
    createStatus.classList.remove("status--success", "status--error");
    if (status) {
      createStatus.classList.add(status);
    }
  };

  const createHint = document.getElementById("create-hint");

  const updateCreateState = () => {
    const requiredFields = [createExternalCaseCode];
    const requiredLabels = ["External case code"];
    const missing = [];
    requiredFields.forEach((field, i) => {
      if (!field || !field.value || field.value.trim() === "") {
        missing.push(requiredLabels[i]);
      }
    });
    const isComplete = missing.length === 0;
    createBtn.disabled = !isComplete || isCreateLoading;
    createBtn.title = createBtn.disabled && missing.length > 0
      ? "Missing: " + missing.join(", ")
      : "";
    if (createHint) {
      createHint.textContent = createBtn.disabled && missing.length > 0 && !isCreateLoading
        ? "Fill required: " + missing.join(", ")
        : "Fill all required fields (*) to enable Create.";
    }
    if (createStatus) {
      createStatus.textContent = createBtn.disabled && missing.length > 0 && !isCreateLoading
        ? "Fill required: " + missing.join(", ")
        : "";
      if (createBtn.disabled && missing.length > 0 && !isCreateLoading) {
        createStatus.classList.remove("status--success", "status--error");
        createStatus.classList.add("status--error");
      }
    }
  };

  const postCreateCase = async () => {
    // eslint-disable-next-line no-console
    console.log("[TOI Case] Create button clicked");
    if (!createBtn) return;
    if (!API_CONFIG.createCasePath) {
      // eslint-disable-next-line no-console
      console.error("[TOI Case] Missing API_CONFIG.createCasePath");
      setCreateStatus("Missing API config.", "status--error");
      return;
    }

    isCreateLoading = true;
    createBtn.disabled = true;
    createBtn.textContent = "Creating...";
    setCreateStatus("Creating case...", "");
    try {
      // eslint-disable-next-line no-console
      console.log("[TOI Case] Building payload...");
      const payload = await buildCreatePayload();
      // eslint-disable-next-line no-console
      console.log("[TOI Case] Create case payload", payload);
      const url = `${API_CONFIG.baseUrl}${API_CONFIG.createCasePath}`;
      // eslint-disable-next-line no-console
      console.log("[TOI Case] POST", url);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": API_CONFIG.apiKey,
        },
        body: JSON.stringify(payload),
      });

      const responseBody = await response.json().catch(() => null);
      const errorMessage = responseBody?.error || `Create failed: ${response.status}`;

      if (!response.ok) {
        throw new Error(errorMessage);
      }

      if (responseBody?.status === "error") {
        throw new Error(errorMessage);
      }

      const caseId = responseBody?.data?.id;
      const detailUrl = responseBody?.data?.detail_url || responseBody?.data?.detailUrl || "";

      emailAlreadyLinked = true;
      if (detailUrl) {
        setOpenCaseState(detailUrl);
      } else {
        fetchLinkStatus();
      }

      if (caseId) {
        setCreateStatus(`Created successfully case: '${caseId}'.`, "status--success");
        showLink();
        setLinkStatus(`Successfully created and linked to case: '${caseId}'.`, "status--success");
      } else {
        setCreateStatus("Created successfully.", "status--success");
        showLink();
        setLinkStatus("Successfully created and linked.", "status--success");
      }
      updateLinkState();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TOI Case] Create case failed", error);
      const msg = error?.message || "";
      const suggestMock =
        /403|Authorization_RequestDenied|Insufficient privileges|getInternetMessageId|getAsync/i.test(msg);
      setCreateStatus(
        suggestMock
          ? "Could not read email (permission?). For local test add ?mock=1 to add-in URL."
          : (msg || "Create failed. Please try again."),
        "status--error"
      );
    } finally {
      isCreateLoading = false;
      createBtn.textContent = "Create";
      updateCreateState();
    }
  };

  const filterCases = () => {
    const term = caseInput.value.trim().toLowerCase();
    if (!term) {
      renderCases(allCases);
      openCaseOptions();
      return;
    }

    const filtered = allCases.filter((caseItem) => {
      const label = caseItem.label.toLowerCase();
      const id = caseItem.id.toLowerCase();
      return label.includes(term) || id.includes(term);
    });
    renderCases(filtered);
    openCaseOptions();
  };

  caseInput.addEventListener("input", async () => {
    const term = caseInput.value.trim();
    if (!term) {
      renderCases(allCases);
      openCaseOptions();
      return;
    }

    if (term.length >= 4) {
      const remoteCases = await fetchCases(term);
      renderCases(remoteCases);
      return;
    }

    filterCases();
  });
  caseInput.addEventListener("focus", () => {
    renderCases(allCases);
    openCaseOptions();
  });
  document.addEventListener("click", (event) => {
    if (!caseCombo.contains(event.target)) {
      closeCaseOptions();
    }
  });

  const showCreate = () => {
    createSection.style.display = "flex";
    linkSection.style.display = "none";
    createShortcut.style.display = "none";
  };

  const showLink = () => {
    createSection.style.display = "none";
    linkSection.style.display = "flex";
    createShortcut.style.display = "inline-flex";
  };

  createShortcut.addEventListener("click", showCreate);
  createClose.addEventListener("click", showLink);
  createBtn.addEventListener("click", postCreateCase);
  linkBtn.addEventListener("click", postLinkToCase);

  [
    createInsuranceCompany,
    createHospital,
    createPolicyNo,
    createPolicyStart,
    createPolicyEnd,
    createPatientName,
    createDateOfBirth,
    createPatientType,
    createCaseCategory,
    createProblemType,
    createCallDate,
      createLocationCountry,
      createLocationCity,
      createDocType,
    createPriority,
    createStatusSelect,
    createExternalCaseCode,
    createPhoneType,
    createPhoneCountry,
    createPhoneNumber,
    createCaseCategory,
    createCallDate,
  ].forEach((field) => {
    if (!field) {
      return;
    }
    const eventName = field.tagName === "SELECT" ? "change" : "input";
    field.addEventListener(eventName, updateCreateState);
  });
  prefillCreateFormForMock();
  updateCreateState();

  if (createCallDate) {
    createCallDate.addEventListener("change", () => {
      if (!createCallDate.value) {
        return;
      }
      const [datePart, timePart] = createCallDate.value.split("T");
      if (!timePart) {
        createCallDate.value = `${datePart}T00:00`;
      }
    });
  }

  const prefillCreateFromEmail = async () => {
    try {
      const emailContext = await getEmailContextCached();
      detectedClaimData = extractClaimData({
        subject: emailContext?.email?.subject || "",
        bodyHtml: emailContext?.email?.body?.content || "",
      });
      prefillCreateFormFromDetected(detectedClaimData);
      if (detectedClaimData?.claimNo && !emailAlreadyLinked) {
        // Feature behavior: auto-open Create view after Claim No is detected.
        showCreate();
      }
      updateCreateState();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TOI Case] Prefill from email failed", error);
    }
  };

  const initDataFlow = async () => {
    await fetchLinkStatus();
    const cases = await fetchCases();
    allCases = cases;
    renderCases(allCases);
    // Check case code by Claim No and prefill create form.
    await bootstrapClaimDetection();
  };
  initDataFlow();
}
